import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const redirectingRef = useRef(false);
  // Track in-flight refresh so concurrent 401s share one refresh call
  const refreshPromiseRef = useRef(null);

  useEffect(() => {
    checkAuth();
  }, []);

  // Axios response interceptor: on 401, try refresh once then retry.
  // Installed once; uses refs so it always sees current state.
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config;

        // Skip auth endpoints to avoid infinite loops
        const isAuthEndpoint = original?.url?.includes('/api/auth/');
        if (
          error.response?.status !== 401 ||
          isAuthEndpoint ||
          original._retried
        ) {
          return Promise.reject(error);
        }

        original._retried = true;

        try {
          // Deduplicate: if a refresh is already running, wait for it
          if (!refreshPromiseRef.current) {
            const base = process.env.REACT_APP_API_BASE_URL || '';
            refreshPromiseRef.current = axios
              .post(`${base}/api/auth/refresh`, {}, { withCredentials: true })
              .finally(() => { refreshPromiseRef.current = null; });
          }
          await refreshPromiseRef.current;
          // Session refreshed — retry the original request
          return axios(original);
        } catch {
          // Refresh failed — force logout
          doLogout();
          return Promise.reject(error);
        }
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const checkAuth = async () => {
    if (redirectingRef.current) return;
    try {
      const base = process.env.REACT_APP_API_BASE_URL || '';
      const response = await axios.get(`${base}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data.user);
    } catch {
      setUser(null);
      redirectingRef.current = true;
      const base = process.env.REACT_APP_API_BASE_URL || '';
      window.location.href = `${base}/api/auth/login`;
    } finally {
      setLoading(false);
    }
  };

  const doLogout = () => {
    setUser(null);
    if (redirectingRef.current) return;
    redirectingRef.current = true;
    const base = process.env.REACT_APP_API_BASE_URL || '';
    window.location.href = `${base}/api/auth/login`;
  };

  const login = (userData) => {
    setUser(userData);
  };

  const logout = () => {
    setUser(null);
    const base = process.env.REACT_APP_API_BASE_URL || '';
    window.location.href = base + '/api/auth/logout';
  };

  const hasRole = (role) => user?.role === role;
  const hasAnyRole = (...roles) => roles.includes(user?.role);

  const isAdmin = () => hasRole('admin');
  const isOperations = () => hasRole('operations');
  const isViewer = () => hasRole('viewer');
  const canEdit = () => hasAnyRole('admin', 'operations');
  const canViewOnly = () => hasRole('viewer');

  const value = {
    user,
    loading,
    login,
    logout,
    hasRole,
    hasAnyRole,
    isAdmin,
    isOperations,
    isViewer,
    canEdit,
    canViewOnly,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
